import _ from 'lodash';
import {
  get,
  post,
  put,
  del, } from 'utils/fetch';
import { createAction, } from 'utils/store';
import { listReducer, createList, } from 'utils/list';
import { withHost } from 'utils/url';
import { getTwitchAuthURL, openAuthWindow } from 'utils/twitch';
import { RequestState } from 'constants/constants';
import { selectCurrentUserId, selectApiHost, selectConfig } from 'store/AppState';


// ======================
// HELPERS
// ====================== 
export const isFreeAccount = account => account && !account?.permissions?.addChannels;
export const canAddChannel = account => !isFreeAccount(account) || (isFreeAccount(account) && account?.channels?.length === 0)
export const getServiceIds = account => _.map(account?.channels, 'serviceId');
export const getChannelIds = account => _.map(account?.channels, 'channelId');
export const getChannel = (account, channelId) => _.find(account?.channels, {channelId});
export const getChannelSettings = (account, channelId) => _.find(account?.channels, { channelId })?.settings;
export const getChatFrequency = account => account?.settings.botFrequency ?? 300000;
export const getChannelFrequency = (account, channelId) => getChannelSettings(account)?.botFrequency;
export const getAllTags = account => account?.tags
export const getTag = (account, tagId) => _.find(account?.tags, { tagId });
export const getChannelTags = (account, channelId) => {
  const tagIds = getChannel(account, channelId)?.tags;
  return getAllTags(account) 
    |> _.keyBy(#, 'tagId') 
    |> _.pick(#, tagIds);
}
export const getChannelIdsByTag = account => account?.tags 
  |> _.keyBy(#, 'tagId') 
  |> _.mapValues(#, (v, tagId) => account.channels 
    |> _.filter(#, channel => _.includes(channel.tags, tagId)) 
    |> _.map(#, 'channelId'));

export const hasChannelTag = (account, channelId, tagId) => getChannelTags(account, channelId) 
  |> _.find(#, { tagId }) 
  |> Boolean(#)

export const getStreamerByChannelId = (account, channelId) => account?.channels
  |> _.find(#, { channelId })
  |> _.get(#, 'serviceName', '');


// ======================
// ACTIONS
// ====================== 
export const FETCH_ACCOUNT = 'FETCH_ACCOUNT';
export const ACCOUNT_UPDATE_SETTINGS = 'ACCOUNT_UPDATE_SETTINGS';
export const FETCH_HEALTH_CHECK = 'FETCH_HEALTH_CHECK';
export const FETCH_ACCOUNT_BOTS = 'FETCH_ACCOUNT_BOTS';
export const ADD_BOT_ACCOUNT = 'ADD_BOT_ACCOUNT';
export const UPDATE_BOT_ACCOUNT = 'UPDATE_BOT_ACCOUNT';
export const REMOVE_BOT_ACCOUNT = 'REMOVE_BOT_ACCOUNT';
export const ADD_TAG = 'ADD_TAG';
export const UPDATE_TAG = 'UPDATE_TAG';
export const DELETE_TAG = 'DELETE_TAG';
export const ADD_CHANNEL_TAG = 'ADD_CHANNEL_TAG';
export const REMOVE_CHANNEL_TAG = 'REMOVE_CHANNEL_TAG';



// ======================
// SELECTORS
// ====================== 
export const selectCurrentAccount = state => state.models.accounts[selectCurrentUserId(state)];
export const selectCurrentAccountId = state => state.app.authUser?.accountId;
export const selectAccountSettings = state => selectCurrentAccount(state)?.settings;
export const selectAccountBots = state => state 
  |> selectCurrentAccount
  |> [ #?.settings?.botUserId, ...(#?.settings?.botUsers ?? []), ]
  |> _.uniq



// ======================
// ENTITY
// ====================== 
const Account = {
  actions: {
    fetchAccount: accountId => (dispatch, getState) => 
      accountId ?? selectCurrentAccountId(getState())
        |> get({
             url: '/api/account/:accountId',
             params: { accountId: # },
             dispatch,
             action: FETCH_ACCOUNT,
           }),


    updateSettings: settings => (dispatch, getState) => {
      let accountId = selectCurrentAccountId(getState());
      return put({
        url: "/api/account/:accountId/settings",
        params: { accountId },
        body: { settings },
        dispatch,
        action: ACCOUNT_UPDATE_SETTINGS,
        host: selectApiHost(getState()),
      });
    },

    getHealthCheck: (accountId, channelId) => (dispatch, getState) =>
      accountId ?? selectCurrentAccountId(getState())
        |> get({
             url: "/api/overlay/:overlayId/channel/:channelId/health_check",
             params: { accountId: #, channelId },
             dispatch,
             action: FETCH_HEALTH_CHECK,
           }),

    fetchChatBots: accountId => (dispatch, getState) => 
      accountId ?? selectCurrentAccountId(getState())
        |> get({
            url: '/api/account/:accountId/bots',
            params: { accountId: # },
            dispatch,
            action: FETCH_ACCOUNT_BOTS,
          }),

    authChatBot: (values, type = 'twitch') => async (dispatch, getState) => {
      let accountId = selectCurrentAccountId(getState());
      return await post({
        url: "/api/account/:accountId/bot/:type/add", 
        params: { accountId, type },
        body: values,
        dispatch, 
        action: ADD_BOT_ACCOUNT,
      });
    },
    
    addTwitchBotAccount: () => async (dispatch, getState) => {
      let state = getState();
      let config = selectConfig(state);
      let redirect = withHost(config.auth.redirect);
      let url = getTwitchAuthURL(config.twitch.client, config.twitch.scopes, redirect);
      let code = await openAuthWindow(url);
      return dispatch(Account.actions.authChatBot({...code, redirect }));
    },

    updateDefaultBotAccount:  (botUserId) => async (dispatch, getState) => {
      let accountId = selectCurrentAccountId(getState());
      let response = await put({
        url: "/api/account/:accountId/bot/:botUserId/default", 
        params: { accountId, botUserId },
        dispatch, 
        action: UPDATE_BOT_ACCOUNT,
      });
    },

    updateChannelBotAccount:  (channelId, botUserId) => async (dispatch, getState) => {
      let accountId = selectCurrentAccountId(getState());
      let response = await put({
        url: "/api/account/:accountId/channel/:channelId/bot/:botUserId", 
        params: { accountId, channelId, botUserId },
        dispatch, 
        action: UPDATE_BOT_ACCOUNT,
      });
    },

    removeBotAccount:  (botUserId) => async (dispatch, getState) => {
      let accountId = selectCurrentAccountId(getState());
      let response = await del({
        url: "/api/account/:accountId/bot/:botUserId/remove", 
        params: { accountId, botUserId },
        dispatch, 
        action: REMOVE_BOT_ACCOUNT,
      });
    },

    createTag: tagName => async (dispatch, getState) => {
      let accountId = selectCurrentAccountId(getState());
      return await post({
        url: "/api/account/:accountId/tag", 
        params: { accountId },
        body: { tagName },
        dispatch, 
        action: ADD_TAG,
      });
    },

    updateTag: (tagId, name) => async (dispatch, getState) => {
      let accountId = selectCurrentAccountId(getState());
      return await put({
        url: "/api/account/:accountId/tag/:tagId", 
        params: { accountId, tagId },
        body: { name },
        dispatch, 
        action: UPDATE_TAG,
      });
    },

    deleteTag: tagId => async (dispatch, getState) => {
      let accountId = selectCurrentAccountId(getState());
      return await del({
        url: "/api/account/:accountId/tag/:tagId", 
        params: { accountId, tagId },
        dispatch, 
        action: DELETE_TAG,
      });
    },

    addChannelTag: (channelId, tagId) => async (dispatch, getState) => {
      let accountId = selectCurrentAccountId(getState());
      return await post({
        url: "/api/account/:accountId/channel/:channelId/tag/:tagId", 
        params: { accountId, channelId, tagId },
        dispatch, 
        action: ADD_CHANNEL_TAG,
      });
    },

    removeChannelTag: (channelId, tagId) => async (dispatch, getState) => {
      let accountId = selectCurrentAccountId(getState());
      return await del({
        url: "/api/account/:accountId/channel/:channelId/tag/:tagId", 
        params: { accountId, channelId, tagId },
        dispatch, 
        action: REMOVE_CHANNEL_TAG,
      });
    },
  },

  spec: {
    accounts: {},
  },

  reducer: (state, type, body, action, json) => {
    if (action.url && action.result !== RequestState.SUCCESS) 
      return state;

    if(body && body.accounts) {
      state = { 
        ...state, 
        accounts:  {
          ...state.accounts,
          ..._.keyBy(body.accounts, 'ownerId'),
        }
      }
    }

    return state;
  },

  listSpec: {
    channelsByAccount: {}
  },

  listReducer: (state, type, body, action, json) => {
    if(action.url && action.result !== RequestState.SUCCESS)
      return state;

    if(body && body.accounts) {
      let channels = _.flatMap(body.accounts, 'channels');
      state = { ...state,
        channelsByAccount: { ...state.channelsByAccount,
          ...(body.accounts 
              |> _.keyBy(#, '_id') 
              |> _.mapValues(#, account => 
                  createList(account.channels 
                    |> _.map(#, v => Object.assign({...v, _id: v.channelId, })), 'channels', '_id')))
        }
      }
    }

    return state;
  },
}
export default Account;
  